import React, { useMemo } from 'react';
import { VerticalOption } from '@amzn/aet-galactus-kit';
import Modal from '@amzn/meridian/modal/modal';
import ModalFooter from '@amzn/meridian/modal/modal-footer';
import Button from '@amzn/meridian/button/button';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import Box from '@amzn/meridian/box';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  coachingModalMessages,
  verticalRequirements,
} from './CoachingModalConstants';
import UserBadgeRow from './UserBadgeRow';
import { CoachingStatsAssociateDetailsOutputExtended } from '../../../../Types/CoachingStats';

interface CoachingModalProps {
  open: boolean;
  onClose: () => void;
  coachingType: 'weekly' | 'monthly';
  vertical: VerticalOption;
  inactiveAssociates: CoachingStatsAssociateDetailsOutputExtended[];
  movedTeamsAssociates: CoachingStatsAssociateDetailsOutputExtended[];
}

interface CoachingContentData {
  title: string;
  verticalRequirementsTitle: string;
  requirements: string[];
}

const CoachingModal: React.FC<CoachingModalProps> = ({
  open,
  onClose,
  coachingType,
  vertical,
  inactiveAssociates,
  movedTeamsAssociates,
}) => {
  const intl = useIntl();
  // Deduplicate users: if a user is in both movedTeamsAssociates and inactiveAssociates,
  // Deduplicate them from the newTeam set, and only show them in the inactive section
  // Inactive takes precedence because we always exclude inactive users before calculating coaching stats
  const deduplicatedNewTeamAssociates = useMemo(() => {
    const inactiveUserIds = new Set(
      inactiveAssociates.map(user => user.userId)
    );

    return movedTeamsAssociates.filter(
      user => !inactiveUserIds.has(user.userId)
    );
  }, [inactiveAssociates, movedTeamsAssociates]);

  const coachingContent = useMemo((): CoachingContentData => {
    const isWeekly = coachingType === 'weekly';
    const title = isWeekly
      ? intl.formatMessage(coachingModalMessages.weeklyCoaching)
      : intl.formatMessage(coachingModalMessages.monthlyCoaching);

    // Get vertical name for display
    const getVerticalDisplayName = (vert: VerticalOption): string => {
      switch (vert) {
        case VerticalOption.AB:
          return 'AB';
        case VerticalOption.Consumer:
          return 'Consumer';
        case VerticalOption.CAP:
          return 'CAP';
        case VerticalOption.D2:
          return 'D2AS';
        case VerticalOption.SDS:
          return 'SDS';
        case VerticalOption.Total:
          return 'Total';
        default:
          return 'Total';
      }
    };

    const verticalName = getVerticalDisplayName(vertical);
    const verticalRequirementsTitle = intl.formatMessage(
      coachingModalMessages.verticalRequiredCoachings,
      { vertical: verticalName }
    );

    // Simply map the requirement keys to their formatted messages
    const requirementsList = verticalRequirements[verticalName].map(
      (messageKey: string) =>
        intl.formatMessage(
          coachingModalMessages[
            messageKey as keyof typeof coachingModalMessages
          ]
        )
    );

    return {
      title,
      verticalRequirementsTitle,
      requirements: requirementsList,
    };
  }, [coachingType, vertical, intl]);

  return (
    <Modal
      title={coachingContent.title}
      open={open}
      onClose={onClose}
      scrollContainer='viewport'
      closeLabel='Close'
      aria-describedby='modal-description'
      width={1000}
      bodySpacingInset={'450 500 500 500'}
    >
      <Box spacingInset='200 none 400 none'>
        <Text type='h100'>
          <FormattedMessage
            {...coachingModalMessages.understandingCoachingTotals}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text id='modal-description'>
          <FormattedMessage
            {...coachingModalMessages.coachingTotalsDescription}
          />
        </Text>
      </Box>

      <Box spacingInset='400 none 400 none'>
        <Text type='h100'>
          <FormattedMessage
            {...coachingModalMessages.understandingWeeklyNature}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text>
          <FormattedMessage
            {...coachingModalMessages.weeklyNatureDescription}
          />
        </Text>
      </Box>

      <Box spacingInset='400 none 400 none'>
        <Text type='h100'>
          <FormattedMessage
            {...coachingModalMessages.verticalRequiredCoachings}
            values={{
              vertical: vertical ? vertical.replace(/_/g, ' ') : 'current',
            }}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text>
          <FormattedMessage
            {...coachingModalMessages.requiredCoachingsDescription}
            values={{
              vertical: vertical ? vertical.replace(/_/g, ' ') : 'current',
            }}
          />
        </Text>
      </Box>
      {coachingContent.requirements.map((requirement, index) => (
        <Box key={index} spacingInset='none none 100 200'>
          <Text>• {requirement}</Text>
        </Box>
      ))}

      <Box spacingInset='450 none 400 none'>
        <Text type='h100'>
          <FormattedMessage {...coachingModalMessages.bottomQuartileDelay} />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text>
          <FormattedMessage
            {...coachingModalMessages.bottomQuartileDelayDescription}
          />
        </Text>
      </Box>

      <Box spacingInset='400 none 400 none'>
        <Text type='h100'>
          <FormattedMessage
            {...coachingModalMessages.movedTeamAssociates}
            values={{ count: deduplicatedNewTeamAssociates.length }}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text>
          <FormattedMessage
            {...coachingModalMessages.movedTeamAssociatesDescription}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <UserBadgeRow
          profiles={deduplicatedNewTeamAssociates}
          maxCount={6}
          modalTitle={intl.formatMessage({
            id: 'cs_perfportal_metrics_moved_team_associates',
            defaultMessage: 'Associates Who Moved Teams',
          })}
        />
      </Box>

      <Box spacingInset='400 none 400 none'>
        <Text type='h100'>
          <FormattedMessage
            {...coachingModalMessages.inactiveAssociates}
            values={{ count: inactiveAssociates.length }}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text>
          <FormattedMessage
            {...coachingModalMessages.inactiveAssociatesDescription}
          />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <UserBadgeRow
          profiles={inactiveAssociates}
          maxCount={6}
          modalTitle={intl.formatMessage({
            id: 'cs_perfportal_metrics_inactive_associates',
            defaultMessage: 'Inactive Associates',
          })}
        />
      </Box>

      <Box spacingInset='400 none 400 none'>
        <Text type='h100'>
          <FormattedMessage {...coachingModalMessages.finalNote} />
        </Text>
      </Box>
      <Box spacingInset='none none 400 none'>
        <Text>
          <FormattedMessage {...coachingModalMessages.finalNoteDescription} />
        </Text>
      </Box>

      <ModalFooter>
        <Row alignmentHorizontal='end' widths='fit'>
          <Button type='primary' size='small' onClick={onClose}>
            <FormattedMessage {...coachingModalMessages.confirm} />
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default CoachingModal;
